import {
  EMAIL_SOURCE_FORMATS,
  type EmailSourceFormat,
} from '@/lib/email-conversion/source-formats/types'

export type { EmailSourceFormat } from './types'
export { EMAIL_SOURCE_FORMATS, isEmailSourceFormat } from './types'

/** Lower-case extensions (no dot) per format; the first is canonical. */
export const EMAIL_SOURCE_FILE_EXTENSIONS: Readonly<
  Record<EmailSourceFormat, ReadonlyArray<string>>
> = {
  html: ['html', 'htm', 'xhtml'],
  mjml: ['mjml'],
  eml: ['eml'],
  'react-email': ['jsx', 'tsx'],
}

/** The media type an inline (data: URL) attachment of each format carries. */
export const EMAIL_SOURCE_MEDIA_TYPE_BY_FORMAT: Readonly<
  Record<EmailSourceFormat, string>
> = {
  html: 'text/html',
  mjml: 'text/mjml',
  eml: 'message/rfc822',
  'react-email': 'text/jsx',
}

/** Every media type that identifies a format (canonical first). */
const EMAIL_SOURCE_MEDIA_TYPES: Readonly<
  Record<EmailSourceFormat, ReadonlyArray<string>>
> = {
  html: ['text/html', 'application/xhtml+xml'],
  mjml: ['text/mjml', 'application/mjml'],
  eml: ['message/rfc822'],
  'react-email': ['text/jsx', 'text/tsx', 'text/x-jsx', 'text/x-tsx'],
}

/** `<input accept>` value for every email-source picker. */
/**
 * A file name travels into the stored receipt (`Imported <name>.`), the
 * email title and the logs, so it is capped like a POSIX path segment.
 */
const EMAIL_SOURCE_FILENAME_MAX_CHARS = 255

export function isValidEmailSourceFilename(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= EMAIL_SOURCE_FILENAME_MAX_CHARS
  )
}

/** Trim and truncate a user-supplied file name to the shared cap. */
export function clampEmailSourceFilename(value: string): string {
  const trimmed = value.trim()
  return trimmed.length > EMAIL_SOURCE_FILENAME_MAX_CHARS
    ? trimmed.slice(0, EMAIL_SOURCE_FILENAME_MAX_CHARS)
    : trimmed
}

export const EMAIL_SOURCE_FILE_ACCEPT = [
  ...EMAIL_SOURCE_FORMATS.flatMap((format) =>
    EMAIL_SOURCE_FILE_EXTENSIONS[format].map((extension) => `.${extension}`)
  ),
  'text/html',
  'application/xhtml+xml',
  'message/rfc822',
].join(',')

/**
 * An MJML document starts with its `<mjml>` root, allowing only whitespace,
 * comments and an XML prolog before it. Anchored on purpose: an ordinary HTML
 * email that mentions `<mjml>` in a comment or a code sample is HTML, and an
 * unanchored match sent it to the MJML compiler, which refused it.
 */
// The comment body is TEMPERED, not lazy: `[\s\S]*?` inside the outer `*`
// lets a leading run of m comments split ~2^m ways, and an import whose root
// is not `<mjml` walks every one (26 comments took 0.5 s, ~4x per two more).
// Outlook conditional-comment preambles hit exactly this shape.
export const MJML_ROOT =
  /^(?:\s|<!--(?:(?!-->)[\s\S])*-->|<\?xml[^>]*>)*<mjml[\s>]/i

const SNIFF_WINDOW_CHARS = 16 * 1024

function normalizeMediaType(mediaType: string | undefined): string {
  return mediaType?.split(';')[0]?.trim().toLowerCase() ?? ''
}

function getFileExtension(filename: string | undefined): string {
  const name = filename?.trim().toLowerCase() ?? ''
  const dot = name.lastIndexOf('.')
  if (dot < 0 || dot === name.length - 1) {
    return ''
  }
  return name.slice(dot + 1)
}

/** Format by file extension first, then by an identifying media type. */
export function detectEmailSourceFormatFromName(
  filename?: string,
  mediaType?: string
): EmailSourceFormat | undefined {
  const extension = getFileExtension(filename)
  if (extension) {
    for (const format of EMAIL_SOURCE_FORMATS) {
      if (EMAIL_SOURCE_FILE_EXTENSIONS[format].includes(extension)) {
        return format
      }
    }
  }
  return emailSourceFormatFromMediaType(mediaType)
}

export function emailSourceFormatFromMediaType(
  mediaType: string | undefined
): EmailSourceFormat | undefined {
  const normalized = normalizeMediaType(mediaType)
  if (!normalized) {
    return
  }
  for (const format of EMAIL_SOURCE_FORMATS) {
    if (EMAIL_SOURCE_MEDIA_TYPES[format].includes(normalized)) {
      return format
    }
  }
}

/** The markup sniff every html-kind import already ran: MJML root or HTML. */
export function sniffMarkupFormat(text: string): 'html' | 'mjml' {
  return MJML_ROOT.test(text) ? 'mjml' : 'html'
}

// RFC 5322 header field name: printable ASCII except ':' and space.
const HEADER_LINE = /^([!-9;-~]+):/
const CONTINUATION_LINE = /^[ \t]+\S/
const MBOX_FROM_LINE = /^From .*$/
const MAX_HEADER_SCAN_LINES = 200

function looksLikeEmlMessage(head: string): boolean {
  const lines = head.split(/\r?\n/, MAX_HEADER_SCAN_LINES)
  let index = 0
  if (lines[0] !== undefined && MBOX_FROM_LINE.test(lines[0])) {
    index = 1
  }
  const names = new Set<string>()
  let headerCount = 0
  for (; index < lines.length; index += 1) {
    const line = lines[index] ?? ''
    if (line.trim() === '') {
      break
    }
    if (headerCount > 0 && CONTINUATION_LINE.test(line)) {
      continue
    }
    const match = line.match(HEADER_LINE)
    if (!match?.[1]) {
      return false
    }
    names.add(match[1].toLowerCase())
    headerCount += 1
  }
  if (headerCount < 2) {
    return false
  }
  return (
    names.has('mime-version') ||
    names.has('content-type') ||
    (names.has('from') && names.has('subject'))
  )
}

const LEADING_DIRECTIVE = /^\s*(?:['"]use (?:client|server)['"];?\s*)?/
// Tempered for the same reason as MJML_ROOT: no lazy body inside the star.
const LEADING_COMMENTS = /^(?:\s|\/\/[^\n]*\n|\/\*(?:(?!\*\/)[\s\S])*\*\/)*/
const MODULE_START =
  /^(?:import\s|export\s|const\s|let\s|var\s|function\s|async\s+function\s)/
const REACT_EMAIL_ROOT =
  /^<(?:Html|Body|Container|Section|Head|Preview|Tailwind)\b/

function looksLikeReactEmailModule(head: string): boolean {
  if (head.includes('@react-email/')) {
    return true
  }
  const stripped = head
    .replace(LEADING_DIRECTIVE, '')
    .replace(LEADING_COMMENTS, '')
  return MODULE_START.test(stripped) || REACT_EMAIL_ROOT.test(stripped)
}

/**
 * Content-based detection for pasted text or an unnamed file. Order matters:
 * an EML message wraps HTML, and a React Email module contains JSX tags.
 */
export function sniffEmailSourceFormat(text: string): EmailSourceFormat {
  const head = text.slice(0, SNIFF_WINDOW_CHARS)
  if (looksLikeEmlMessage(head)) {
    return 'eml'
  }
  if (sniffMarkupFormat(head) === 'mjml') {
    return 'mjml'
  }
  if (looksLikeReactEmailModule(head)) {
    return 'react-email'
  }
  return 'html'
}

/**
 * The extension wins over the content, with ONE exception kept from the
 * html-only days: a `.html` file whose root is `<mjml>` compiles as MJML.
 */
export function resolveEmailSourceFormat(args: {
  filename?: string
  mediaType?: string
  text: string
}): EmailSourceFormat {
  const named = detectEmailSourceFormatFromName(args.filename, args.mediaType)
  if (named === 'html' && sniffMarkupFormat(args.text) === 'mjml') {
    return 'mjml'
  }
  return named ?? sniffEmailSourceFormat(args.text)
}

const FORMAT_LABELS: Readonly<Record<EmailSourceFormat, string>> = {
  html: 'HTML',
  mjml: 'MJML',
  eml: 'Email message (.eml)',
  'react-email': 'React Email',
}

export function describeEmailSourceFormat(format: EmailSourceFormat): string {
  return FORMAT_LABELS[format]
}

const ATTACHMENT_LABELS: Readonly<Record<EmailSourceFormat, string>> = {
  html: 'HTML email source',
  mjml: 'MJML source',
  eml: 'EML message',
  'react-email': 'React Email source',
}

export function describeEmailSourceAttachment(
  format: EmailSourceFormat
): string {
  return ATTACHMENT_LABELS[format]
}

/** `welcome.EML` → `welcome`; names without a listed extension are unchanged. */
export function stripEmailSourceExtension(filename: string): string {
  const extension = getFileExtension(filename)
  if (!extension) {
    return filename
  }
  const isListed = EMAIL_SOURCE_FORMATS.some((format) =>
    EMAIL_SOURCE_FILE_EXTENSIONS[format].includes(extension)
  )
  return isListed ? filename.slice(0, -(extension.length + 1)) : filename
}
